import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

interface VulnerabilityResult {
  id?: number;
  rule_id: string;
  rule_name: string;
  severity: string;
  description: string;
  file?: string;
  line?: number;
  function?: string;
  detail: {
    contract?: string;
    type?: string;
    [key: string]: any;
  };
}

interface AnalysisResultsProps {
  results: {
    staticAnalysis: VulnerabilityResult[];
    symbolicExecution: VulnerabilityResult[];
  };
  onSelectIssue?: (issue: VulnerabilityResult) => void;
}

const columns: GridColDef[] = [
  { field: 'rule_name', headerName: 'Type', width: 150 },
  { field: 'severity', headerName: 'Severity', width: 120 },
  { field: 'description', headerName: 'Description', width: 400 },
  { 
    field: 'location', 
    headerName: 'Location', 
    width: 200,
    valueGetter: (params) => {
      const vuln = params.row as VulnerabilityResult;
      const parts = [];
      if (vuln.detail?.contract) parts.push(vuln.detail.contract);
      if (vuln.function) parts.push(vuln.function);
      if (vuln.line) parts.push(`Line ${vuln.line}`);
      return parts.join(':') || 'N/A';
    }
  },
];

const AnalysisResults: React.FC<AnalysisResultsProps> = ({ results, onSelectIssue }) => {
  const staticRows = results.staticAnalysis.map((vuln, index) => ({
    ...vuln,
    id: index,
  }));

  const symbolicRows = results.symbolicExecution.map((vuln, index) => ({
    ...vuln,
    id: index + staticRows.length,
  }));

  const renderDataGrid = (title: string, rows: VulnerabilityResult[]) => (
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Box sx={{ height: 300, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 5 },
            },
          }}
          pageSizeOptions={[5, 10]}
          disableRowSelectionOnClick
          onRowClick={(params) => {
            onSelectIssue && onSelectIssue(params.row as VulnerabilityResult);
          }}
        />
      </Box>
    </Grid>
  );

  return (
    <Grid container spacing={3}>
      {renderDataGrid('Static Analysis Results', staticRows)}
      {renderDataGrid('Symbolic Execution Results', symbolicRows)}
    </Grid>
  );
};

export default AnalysisResults;